<template>
  <div class="date-cell-container d-flex align-items-center">
    <!-- View Mode. Stays rendered while the picker is open: the picker floats
         over the table rather than replacing the cell's contents. -->
    <div @dblclick="startEdit" class="date-display"
      :class="{ 'text-muted': !dateValue, 'date-display--editing': isEditing }">
      {{ formattedDate }}
      <font-awesome-icon
        v-if="isAdmin"
        :icon="['fas', 'pen']"
        class="ml-2 edit-icon"
        size="xs"
      />
      <!-- Clearing is its own action: the datepicker can only pick a date, so
           without this a date entered by mistake could be corrected but never
           removed. Shown only where there is a value to clear, and stopped from
           reaching the cell's dblclick so it cannot open the picker it just
           emptied. -->
      <button
        v-if="isAdmin && clearable && hasValue"
        type="button"
        class="date-clear-btn"
        :title="`Clear ${label || 'date'}`"
        :aria-label="`Clear ${label || 'date'}`"
        @click.stop="clearDate"
        @dblclick.stop
      >
        <font-awesome-icon :icon="['fas', 'times']" size="xs" />
      </button>
    </div>

    <!-- Edit Mode. The calendar is moved onto <body> and positioned against
         this cell: Tabulator clips cell overflow and scrolls the table body,
         so a picker left inside the cell is cut down to a sliver of itself. -->
    <div v-if="isEditing" ref="panel" class="date-picker-panel" :style="panelStyle" @mousedown.stop @dblclick.stop>
      <div class="date-picker-head">{{ label || 'Select date' }}</div>
      <b-calendar
        v-model="editValue"
        locale="en-US"
        block
        hide-header
        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
        @selected="onDateSelected"
      ></b-calendar>
      <div class="date-picker-foot">
        <span class="date-picker-preview">{{ editValue ? formatToCustomStr(editValue) : 'Pick Date' }}</span>
        <button type="button" class="date-picker-cancel" @click="closeEdit">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DateCell",
  props: {
    row: { type: Object, required: true },
    field: { type: String, required: true },
    isAdmin: { type: Boolean, default: false },
    // Off by default: only the columns that opt in get a clear button, so the
    // dates a row is expected to carry cannot be emptied by a stray click.
    clearable: { type: Boolean, default: false },
    // Column label, for the button's tooltip — the same field is "Leased Date"
    // on a rental project and "Sold Date" on a strata one.
    label: { type: String, default: "" }
  },
  data() {
    return {
      isEditing: false,
      editValue: null, // Datepicker uses YYYY-MM-DD
      // Fixed coordinates for the body-level panel, recomputed on each open.
      panelStyle: { top: '0px', left: '0px' }
    };
  },
  computed: {
    dateValue() {
      return this.row[this.field];
    },
    // "-" and "" are both how an unset date arrives, so neither counts as a
    // value the button should offer to clear.
    hasValue() {
      const v = this.dateValue;
      return v != null && v !== "" && v !== "-";
    },
    formattedDate() {
      if (!this.dateValue || this.dateValue === "-") return "-";
      // Ensure if it's already in 31-Oct-25 format it just displays,
      // otherwise format it.
      return this.dateValue.includes('-') && isNaN(this.dateValue[0]) === false
        ? this.dateValue
        : this.formatToCustomStr(this.dateValue);
    }
  },
  beforeDestroy() {
    this.teardownPanel();
  },
  methods: {
    startEdit() {
      if (!this.isAdmin) return;

      const rawValue = this.dateValue;
      if (rawValue && rawValue !== "-") {
        // If stored as '31-Oct-25', parse it back to 'YYYY-MM-DD' for the picker
        this.editValue = this.parseCustomToISO(rawValue);
      } else {
        this.editValue = null;
      }
      this.isEditing = true;
      this.$nextTick(this.openPanel);
    },

    // Re-parents the panel to <body> so no ancestor can clip it, then anchors
    // it to the cell and flips it above when there is no room below.
    openPanel() {
      const panel = this.$refs.panel;
      if (!panel) return;
      if (panel.parentNode !== document.body) document.body.appendChild(panel);

      const rect = this.$el.getBoundingClientRect();
      const width = panel.offsetWidth || 300;
      const height = panel.offsetHeight || 340;
      const gap = 4;

      let top = rect.bottom + gap;
      if (top + height > window.innerHeight && rect.top - gap - height > 0) {
        top = rect.top - gap - height;
      }
      top = Math.max(gap, Math.min(top, window.innerHeight - height - gap));

      let left = rect.left;
      left = Math.max(gap, Math.min(left, window.innerWidth - width - gap));

      this.panelStyle = { top: `${top}px`, left: `${left}px` };

      document.addEventListener("mousedown", this.onDocumentMouseDown, true);
      document.addEventListener("keydown", this.onDocumentKeyDown, true);
      // The cell moves with the table, so a scroll would leave the panel
      // hanging over the wrong row — close instead of chasing it.
      window.addEventListener("scroll", this.closeEdit, true);
      window.addEventListener("resize", this.closeEdit, true);
    },

    closeEdit() {
      if (!this.isEditing) return;
      this.teardownPanel();
      this.isEditing = false;
    },

    teardownPanel() {
      document.removeEventListener("mousedown", this.onDocumentMouseDown, true);
      document.removeEventListener("keydown", this.onDocumentKeyDown, true);
      window.removeEventListener("scroll", this.closeEdit, true);
      window.removeEventListener("resize", this.closeEdit, true);

      // The panel sits on <body>, outside this component's subtree, so a cell
      // rebuild would otherwise leave it behind as an orphan.
      const panel = this.$refs.panel;
      if (panel && panel.parentNode === document.body) {
        document.body.removeChild(panel);
      }
    },

    onDocumentMouseDown(event) {
      const panel = this.$refs.panel;
      if (panel && panel.contains(event.target)) return;
      if (this.$el.contains(event.target)) return;
      this.closeEdit();
    },

    onDocumentKeyDown(event) {
      if (event.key === "Escape") this.closeEdit();
    },

    // Emptied through the same event the picker uses, so RpdTable's one handler
    // syncs the row and flags it edited either way — the cleared value only
    // reaches the database when the row is saved, like every other edit here.
    clearDate() {
      if (!this.isAdmin || !this.clearable) return;

      this.$set(this.row, this.field, "");
      this.row.isEdited = true;
      this.closeEdit();
      this.editValue = null;

      this.$emit("date-updated", {
        row: this.row,
        field: this.field,
        value: ""
      });
    },

    onDateSelected(newDate) {
      if (newDate) {
        // Convert YYYY-MM-DD -> 31-Oct-25
        const customFormatted = this.formatToCustomStr(newDate);

        this.row[this.field] = customFormatted;
        this.row.isEdited = true;

        this.$emit('date-updated', {
          row: this.row,
          field: this.field,
          value: customFormatted
        });
      }
      this.closeEdit();
    },

    // Helper: Converts YYYY-MM-DD to 31-Oct-25
    formatToCustomStr(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr + "T12:00:00"); // Avoid timezone shifts
      const day = String(date.getDate()).padStart(2, '0');
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);

      return `${day}-${month}-${year}`;
    },

    // Helper: Converts 31-Oct-25 back to YYYY-MM-DD for the calendar
    parseCustomToISO(customStr) {
      const parts = customStr.split("-");
      if (parts.length !== 3) return customStr;

      const day = parts[0];
      const monthStr = parts[1];
      const yearShort = parts[2];

      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthIndex = monthNames.indexOf(monthStr);
      const month = String(monthIndex + 1).padStart(2, '0');

      const year = parseInt(yearShort) < 70 ? `20${yearShort}` : `19${yearShort}`;

      return `${year}-${month}-${day}`;
    }
  }
};
</script>

<style scoped>
/* Include the CSS fixes from the previous step here */
.date-cell-container {
  min-height: 30px;
  width: 100%;
}
/* Body-level picker: fixed so it escapes Tabulator's scrolling body, above
   the table and any sticky header it would otherwise sit under. */
.date-picker-panel {
  position: fixed;
  z-index: 10000;
  width: 290px;
  padding: 8px;
  border: 1px solid var(--rpd-border, #e3e6ea);
  border-radius: 8px;
  background: var(--rpd-surface, #fff);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}
.date-picker-head {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--rpd-text-muted, #8a94a0);
  margin-bottom: 6px;
}
.date-picker-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.date-picker-preview {
  font-size: 12px;
  font-weight: 600;
  color: var(--rpd-primary, #0b36dc);
}
.date-picker-cancel {
  border: none;
  background: transparent;
  padding: 2px 4px;
  font-size: 12px;
  color: var(--rpd-text-muted, #8a94a0);
  cursor: pointer;
}
.date-picker-cancel:hover {
  color: var(--rpd-text, #22262b);
}
.date-display--editing {
  font-weight: 600;
}
html.dark .date-picker-panel {
  border-color: var(--rpd-border, #3a4049);
  background: var(--rpd-surface, #1e2227);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}
html.dark .date-picker-panel ::v-deep .b-calendar-grid {
  background: transparent;
  color: inherit;
}
.date-display:hover .edit-icon {
  color: #0b36dc;
}
.edit-icon {
  color: #ccc;
}
.date-clear-btn {
  margin-left: 6px;
  padding: 0 3px;
  border: none;
  background: transparent;
  line-height: 1;
  color: #ccc;
  cursor: pointer;
}
.date-display:hover .date-clear-btn {
  color: #8a8a8a;
}
.date-clear-btn:hover {
  color: #dc3545 !important;
}
</style>
